import { MoveIcon, Trash2Icon, UsersIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Sheet } from '@/components/ui/sheet';
import { GetAgendaSlotItemResponse } from '@/hooks/queries/use-get-office-agendas-slots';
import { useAuthorization } from '@/hooks/use-authorization';
import { useDateFnsLocale } from '@/hooks/use-date-fns-locale';
import { isSlotWithAssistant } from '@/utils/slot-assistant';
import { selectSheetViewModel } from '../sheet.viewmodel';
import { formatSlotSchedule } from '../slot-sheet/format-slot-schedule';
import { SlotAssistantHoursSection } from '../slot-sheet/slot-assistant-hours-section';
import { SlotSheetHeader } from '../slot-sheet/slot-sheet-header';
import { SlotSheetMetaRow } from '../slot-sheet/slot-sheet-meta-row';
import { SlotSheetSection } from '../slot-sheet/slot-sheet-section';
import {
  SlotSheetActions,
  SlotSheetBody,
  SlotSheetContent,
} from '../slot-sheet/slot-sheet-shell';
import { ViewAdminAgendaSlotSectionAccounting } from './sections/view-admin-agenda-slot-section-accounting';
import { ViewAdminAgendaSlotSectionBooking } from './sections/view-admin-agenda-slot-section-booking';
import { ViewAdminAgendaSlotSectionReservations } from './sections/view-admin-agenda-slot-section-reservations';
import { ViewAdminAgendaSlotSectionSlot } from './sections/view-admin-agenda-slot-section-slot';
import {
  selectViewAdminAgendaSlotSheetViewModel,
  ViewAdminAgendaSlotSheetApi,
  ViewAgendaSlotEventOccurrence,
} from './view-admin-agenda-slot-sheet.viewmodel';

export const ViewAdminAgendaSlotSheet = ({
  slot,
  officeId,
  eventOccurrence,
  onAfterDelete,
  onAfterBooked,
  onAfterUpdated,
  onAfterUpdateReservation,
}: {
  slot: GetAgendaSlotItemResponse;
  officeId: string;
  eventOccurrence: ViewAgendaSlotEventOccurrence;
} & Partial<ViewAdminAgendaSlotSheetApi>) => {
  const { t } = useTranslation();
  const locale = useDateFnsLocale();
  const { canEditSlots, canDeleteSlots, canImportAccountingFiles } =
    useAuthorization();
  const canEdit = canEditSlots();
  const canDelete = canDeleteSlots();
  const canViewAccounting = canImportAccountingFiles();

  const sheetViewModel = selectSheetViewModel({
    sheetName: 'ViewAdminAgendaSlotSheet',
  });

  const viewModel = selectViewAdminAgendaSlotSheetViewModel({
    slot,
    officeId,
    eventOccurrence,
    sheetViewModel,
    onAfterDelete: onAfterDelete ?? (() => {}),
    onAfterBooked: onAfterBooked ?? (() => {}),
    onAfterUpdated: onAfterUpdated ?? (() => {}),
    onAfterUpdateReservation: onAfterUpdateReservation ?? (() => {}),
  });

  const schedule = formatSlotSchedule({
    start: eventOccurrence.start,
    end: eventOccurrence.end,
    allDay: eventOccurrence.allday,
    locale,
  });

  const showAssistantHours = isSlotWithAssistant({
    withAssistant: viewModel.information.withAssistant,
    forceWithoutAssistant: viewModel.information.forceWithoutAssistant,
    slotType: viewModel.information.slotType,
  });

  return (
    <Sheet
      open={sheetViewModel.data.show}
      onOpenChange={sheetViewModel.api.onOpenChange}
    >
      <SlotSheetContent size="detail">
        <SlotSheetHeader
          status={viewModel.information.reservationStatus.status}
          title={t(viewModel.title.key, viewModel.title.params)}
          dateLabel={schedule.dateLabel}
          timeLabel={schedule.timeLabel}
        >
          <SlotSheetMetaRow
            id={slot.id}
            updatedAt={slot.updatedAt}
          />
        </SlotSheetHeader>

        <SlotSheetBody>
          <ViewAdminAgendaSlotSectionSlot
            viewModel={viewModel}
            timeLabel={schedule.timeLabel}
            canEdit={canEdit}
          />

          {showAssistantHours && (
            <SlotAssistantHoursSection
              assistantPeriods={slot.assistantPeriods ?? []}
              legacyWorkPeriods={slot.medicalAssistantWorkPeriods}
            />
          )}

          {viewModel.reservation ? (
            <ViewAdminAgendaSlotSectionReservations
              reservation={viewModel.reservation}
              canEdit={canEdit}
            />
          ) : (
            <>
              {viewModel.pendingInvitation && (
                <SlotSheetSection
                  icon={UsersIcon}
                  title={t('view-admin-agenda-slot-sheet.reservation.title')}
                >
                  <div className="flex items-center gap-2 rounded-lg border p-3">
                    <span className="text-muted-foreground text-sm italic">
                      {viewModel.pendingInvitation.firstName}{' '}
                      {viewModel.pendingInvitation.lastName}
                    </span>
                    <Badge
                      variant="outline"
                      className="text-xs"
                    >
                      {t('slot-card-list.card.invited')}
                    </Badge>
                  </div>
                </SlotSheetSection>
              )}

              {canEdit && (
                <ViewAdminAgendaSlotSectionBooking viewModel={viewModel} />
              )}
            </>
          )}

          {canViewAccounting && (
            <ViewAdminAgendaSlotSectionAccounting viewModel={viewModel} />
          )}
        </SlotSheetBody>

        {(canEdit || canDelete) && (
          <SlotSheetActions className="flex flex-col gap-2">
            {canEdit && viewModel.moveSlot.enabled && (
              <Button
                variant="outline"
                className="w-full"
                onClick={viewModel.moveSlot.api.open}
              >
                <MoveIcon
                  className="size-4"
                  aria-hidden="true"
                />
                {t('view-admin-agenda-slot-sheet.btn.move-slot')}
              </Button>
            )}
            {canDelete && (
              <Button
                variant="destructive"
                className="w-full"
                onClick={viewModel.deleteSlot.api.open}
              >
                <Trash2Icon
                  className="size-4"
                  aria-hidden="true"
                />
                {t('view-admin-agenda-slot-sheet.btn.delete-slot')}
              </Button>
            )}
          </SlotSheetActions>
        )}
      </SlotSheetContent>
    </Sheet>
  );
};
